import React, { useCallback, useState } from 'react';

import checkTokens from '@amzn/meridian-tokens/base/icon/check-large';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import documentTokens from '@amzn/meridian-tokens/base/icon/export-large';
import externalTokens from '@amzn/meridian-tokens/base/icon/export-small';
import linkTokens from '@amzn/meridian-tokens/base/icon/link';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import DataTable, { DataTableCell, DataTableRow } from '@amzn/meridian/data-table';
import Divider from '@amzn/meridian/divider';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { IDocumentDetail } from 'src/components/Agreements/DocumentDetail/DocumentDetail';
import { EntityLink, ServiceTag } from 'src/components/Agreements/Shared';
import { goToDocument } from 'src/components/Agreements/Shared/utils';
import { ROUTES } from 'src/constants/routes';

export const ViewDocumentDetail: React.FC<IDocumentDetail> = (props) => {
    const [didCopy, setDidCopy] = useState(false);

    const onShare = useCallback(() => {
        const path = [ROUTES.AGREEMENTS.INDEX, props.tenantId, props.documentId].join('/');
        const link = `${window.location.protocol}//${window.location.host}${path}`;
        navigator.clipboard
            .writeText(link)
            .then(() => setDidCopy(true))
            .catch((err) => {
                props.setErrorMessage(JSON.stringify(err));
            });
    }, [window.location.host, props.documentId]);

    const prefix = props.showPrefix ? 'View document: ' : '';
    const heading = `${prefix}${props.data.resolvedName}`;

    const infoSection = (
        <Column>
            <Row alignmentVertical={'center'} alignmentHorizontal={'justify'} wrap={'down'}>
                <Row minWidth={360}>
                    <Heading level={5}>{heading}</Heading>
                </Row>
                <Row widths={['fill']}>
                    <Button
                        onClick={() =>
                            goToDocument(props.documentId, props.tenantId, props.setLoading, props.setErrorMessage)
                        }
                        type={'primary'}
                    >
                        Open Document
                        <Icon tokens={documentTokens} />
                    </Button>
                    <Tooltip position={'start'} title={'Get a shareable link'}>
                        <Button type={'icon'} aria-label={'share-link'} onClick={onShare}>
                            <Icon tokens={didCopy ? checkTokens : linkTokens} />
                        </Button>
                    </Tooltip>
                    <Tooltip position={'start'} title={'Edit details'}>
                        <Button type={'icon'} aria-label={'edit'} onClick={() => props.setEditMode(true)}>
                            <Icon tokens={editTokens} />
                        </Button>
                    </Tooltip>
                    <Tooltip position={'start'} title={'Back to all documents'}>
                        <Button type={'icon'} aria-label={'close'} onClick={props.onClose}>
                            <Icon tokens={closeSmallTokens} />
                        </Button>
                    </Tooltip>
                </Row>
            </Row>
            {(props.data.friendlyName || props.data.description) && <Divider />}
            {props.data.friendlyName && (
                <Text color={'secondary'} type={'b100'}>{`Original file name: ${props.data.name}`}</Text>
            )}
            {props.data.description && [
                <Heading level={6}>Description</Heading>,
                <Text color={'secondary'} type={'b200'}>
                    {props.data.description}
                </Text>
            ]}
        </Column>
    );

    const classificationSection = (
        <DataTable showRowDividers={true} size={'small'}>
            <DataTableRow>
                <DataTableCell>Licensors</DataTableCell>
                <DataTableCell>
                    <Text type={'b200'}>
                        <Column spacing={'100'}>
                            {props.data.licensors.map((licensor) => (
                                <Row widths={'fit'}>
                                    <EntityLink name={licensor} key={licensor} />
                                </Row>
                            ))}
                        </Column>
                    </Text>
                </DataTableCell>
            </DataTableRow>
            <DataTableRow>
                <DataTableCell>Services</DataTableCell>
                <DataTableCell>
                    <Row>
                        {props.data.services.map((service) => (
                            <ServiceTag name={service} key={service} />
                        ))}
                    </Row>
                </DataTableCell>
            </DataTableRow>
            <DataTableRow>
                <DataTableCell>Start Date</DataTableCell>
                <DataTableCell>{props.data.startDate ?? '-'}</DataTableCell>
            </DataTableRow>
            <DataTableRow>
                <DataTableCell>End Date</DataTableCell>
                <DataTableCell>{props.data.endDate ?? '-'}</DataTableCell>
            </DataTableRow>
        </DataTable>
    );

    const referencesSection = (
        <Column>
            {Object.entries(props.data.links ?? {}).map(([title, link]) => {
                return (
                    <Text type={'b400'}>
                        <Link target={'_blank'} href={link}>
                            {title}
                            &nbsp;
                            <Icon tokens={externalTokens} />
                        </Link>
                    </Text>
                );
            })}
        </Column>
    );

    return (
        <Column height={'100%'}>
            {infoSection}
            <Divider />
            <Heading level={5}>Classification</Heading>
            {classificationSection}
            <Divider />
            <Heading level={5}>References</Heading>
            {referencesSection}
        </Column>
    );
};
